import { useCallback, useRef, useState, type ReactNode } from 'react';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';

interface UseConfirmBeforeCloseReturn {
  askBeforeClose: (onClose: () => void) => void;
  discardChangesDialog: ReactNode;
}

export function useConfirmBeforeClose(hasUnsavedChanges: boolean): UseConfirmBeforeCloseReturn {
  const [open, setOpen] = useState(false);
  const pendingCloseRef = useRef<(() => void) | null>(null);

  const askBeforeClose = useCallback(
    (onClose: () => void) => {
      if (!hasUnsavedChanges) {
        onClose();
        return;
      }
      pendingCloseRef.current = onClose;
      setOpen(true);
    },
    [hasUnsavedChanges],
  );

  function handleDiscard() {
    setOpen(false);
    pendingCloseRef.current?.();
    pendingCloseRef.current = null;
  }

  function handleDialogOpenChange(nextOpen: boolean) {
    if (nextOpen) return;
    setOpen(false);
    pendingCloseRef.current = null;
  }

  const discardChangesDialog = (
    <DiscardChangesDialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      onDiscard={handleDiscard}
    />
  );

  return { askBeforeClose, discardChangesDialog };
}
